'use client';

import React, { type ComponentPropsWithRef } from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { SparkleIcon } from '@depop/web-ui-kit/Icons/SparkleIcon';
import { useResponsive } from '@depop/web-ui-kit/useResponsive';
import clsx from 'clsx';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { backgroundImages } from './constants';
import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

const cloudfrontHost = process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST;
const sfyModalParam = new URLSearchParams('showSfyModal=1');

export function TeaseSuggestedForYou({
  className,
  trackingEvent,
  ...rest
}: { trackingEvent: string } & ComponentPropsWithRef<'div'>) {
  const [isAuthenticated] = useCurrentUser();

  const t = useTranslations('common');
  const { sendActivityTrackerEvent } = useActivityTracker();
  const { isMaxSmall } = useResponsive();

  const path = usePathname();
  const queryParams = useSearchParams();

  const appendedQueryParams = new URLSearchParams([
    ...sfyModalParam,
    ...queryParams,
  ]);

  const redirectUrl = `${path}?${appendedQueryParams.toString()}`;

  const signupUrl = `/signup/?redirect=${encodeURIComponent(redirectUrl)}`;

  if (isAuthenticated) {
    return null;
  }

  function handleSignupClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CTA_ACTION, {
      type: trackingEvent,
      schemaVersion: '1.0',
    });
  }

  return (
    <div
      role="complementary"
      className={clsx(styles.container, className)}
      aria-labelledby="tease-sfy-title"
      {...rest}
    >
      <img
        className={styles.mobileImage}
        src={`${cloudfrontHost}/web/assets/homepage/combined-sfy-stack.png`}
        alt=""
      />

      <div className={styles.backgroundImages}>
        <div className={styles.leftImages}>
          {backgroundImages.left.map((imageName) => (
            <img
              key={imageName}
              className={styles.imageItem}
              src={`${cloudfrontHost}/web/assets/homepage/${imageName}`}
              alt=""
            />
          ))}
        </div>
        <div className={styles.rightImages}>
          {backgroundImages.right.map((imageName) => (
            <img
              key={imageName}
              className={styles.imageItem}
              src={`${cloudfrontHost}/web/assets/homepage/${imageName}`}
              alt=""
            />
          ))}
        </div>
      </div>

      <div className={styles.content}>
        <SparkleIcon size={24} />

        <Heading variant="md" id="tease-sfy-title" className={styles.heading}>
          {t('TeaseSuggestedForYou.Heading')}
        </Heading>
        <Body variant="sm-regular" className={styles.subheading}>
          {t('TeaseSuggestedForYou.Subheading')}
        </Body>
        <ExtendedLink
          href={signupUrl}
          className={styles.ctaWrapper}
          onClick={handleSignupClick}
        >
          <ButtonExperiment
            as="span"
            size={isMaxSmall ? 'regular' : 'large'}
            fullWidth
          >
            {t('TeaseSuggestedForYou.SignUpButton')}
          </ButtonExperiment>
        </ExtendedLink>
      </div>
    </div>
  );
}
